import { useState, useMemo, memo, useEffect } from 'react';
import {
  KeyboardDoubleArrowRight,
  KeyboardDoubleArrowLeft,
} from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from 'store/hook';
import type { SidebarItemConfig, SidebarUser } from './types';
import { SidebarItem } from './SidebarItem';
import { SidebarContainer, MenuButton, NavList } from './NewSidebar.styles';
import {
  hasRequiredRole,
  isInEnabled,
  isEnabledForEnvironment,
  mergeNavigationWithDbConfig,
  type AppModuleKey,
} from 'components/Route/utils';
import { type NavigationConfigMergedParentAndChildUnionType } from 'views/Settings/NavigationMap/types/types';
import { clientThemePalette, CLIENT_THEME } from 'utils/clientTheme';
import { isDarkTheme } from 'utils/theme';
import { useLocation } from 'react-router-dom';
import { updateSelectedModuleState } from 'store/app.slice';

interface NavPathMatcher {
  rawPath: string;
  module: AppModuleKey | null;
  specificity: number;
  matcher: RegExp;
}

const escapeRegExp = (value: string) =>
  value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const toPathMatcher = (rawPath: string): RegExp => {
  const normalized = rawPath.replace(/^\/+|\/+$/g, '');
  const segments = normalized ? normalized.split('/') : [];

  const pattern = segments
    .map((segment) => {
      if (segment.startsWith(':')) return '[^/]+';
      return escapeRegExp(segment);
    })
    .join('/');

  return new RegExp(`^/${pattern}(?:/|$)`);
};

const joinPath = (basePath: string, nextPath?: string) => {
  const base = basePath.replace(/\/+$/g, '');
  const next = (nextPath ?? '').replace(/^\/+|\/+$/g, '');
  if (!next) return base || '';
  if (!base) return `/${next}`;
  return `${base}/${next}`;
};

const buildNavMatchers = (items: SidebarItemConfig[]): NavPathMatcher[] => {
  const matchers: NavPathMatcher[] = [];

  const walk = (nodes: SidebarItemConfig[], basePath: string) => {
    nodes.forEach((node) => {
      const fullPath = node.path ? joinPath(basePath, node.path) : basePath;

      if (node.path) {
        const normalized = fullPath.replace(/^\/+|\/+$/g, '');
        const segments = normalized ? normalized.split('/') : [];
        const staticSegments = segments.filter((s) => !s.startsWith(':'));
        const specificity = segments.length * 10 + staticSegments.length;

        matchers.push({
          rawPath: fullPath,
          module: node.module ?? null,
          specificity,
          matcher: toPathMatcher(fullPath),
        });
      }

      if (node.children && node.children.length > 0) {
        walk(node.children, fullPath);
      }
    });
  };

  walk(items, '');
  return matchers;
};

const inferModuleFromNav = (
  pathname: string,
  matchers: NavPathMatcher[]
): AppModuleKey | null => {
  let best: NavPathMatcher | null = null;

  for (const candidate of matchers) {
    if (!candidate.module) continue; // common routes shouldn't override selected module
    if (!candidate.matcher.test(pathname)) continue;
    if (
      !best ||
      candidate.specificity > best.specificity ||
      (candidate.specificity === best.specificity &&
        candidate.rawPath.length > best.rawPath.length)
    ) {
      best = candidate;
    }
  }

  return best?.module ?? null;
};

const filterByModule = (
  items: SidebarItemConfig[],
  selectedModule: AppModuleKey | null
): SidebarItemConfig[] => {
  if (!selectedModule) return items;
  return items
    .map((item) => {
      const nextChildren = item.children
        ? filterByModule(item.children, selectedModule)
        : [];
      const isCommon = item.module == null;
      const isInModule = item.module === selectedModule;
      if (isCommon || isInModule || nextChildren.length > 0) {
        return { ...item, children: nextChildren };
      }
      return null;
    })
    .filter(Boolean) as SidebarItemConfig[];
};

const filterVisibleItems = (
  items: NavigationConfigMergedParentAndChildUnionType[],
  user: SidebarUser
): SidebarItemConfig[] => {
  return items
    .filter((item) => {
      const isHidden = item.invisible;
      const isVisibleByRole = hasRequiredRole(user.roles, item.roles);
      const isEnabled = isInEnabled(item.enabledFor, user?.customerCode);
      const isEnabledInEnv = isEnabledForEnvironment(item.enabledEnvironments);
      return (
        !isHidden &&
        isVisibleByRole &&
        (item?.enabledFor && item?.enabledFor?.length > 0 ? isEnabled : true) &&
        isEnabledInEnv
      );
    })
    .map((item) => {
      return {
        id: item.id,
        label: item.label,
        icon: item.icon,
        path: item.path,
        roles: item.roles,
        showPro: item.showPro,
        module: (item.module ?? null) as AppModuleKey | null,
        children: item.children ? filterVisibleItems(item.children, user) : [],
      };
    })
    .filter(
      (item) =>
        Boolean(item.path) || (item.children && item.children.length > 0)
    );
};

export const NewSidebar = memo(() => {
  const [iscollapsed, setiscollapsed] = useState(false);
  const location = useLocation();
  const dispatch = useAppDispatch();
  const selectedModule = useAppSelector(
    (state) => state.appReducer.selectedModule
  );
  const navigationConfigData = useAppSelector(
    (state) => state.navigationConfigReducer.data
  );

  const toggleSidebar = () => {
    setiscollapsed(!iscollapsed);
  };
  const email = useAppSelector((state) => state.authReducer.email);
  const customerCode = useAppSelector(
    (state) => state.authReducer.customer.code
  );
  const role = useAppSelector((state) => state.authReducer.role);
  const theme = useAppSelector((state) => state.authReducer).customer.theme;
  const enableDarkTheme = isDarkTheme(theme);

  const colorMode = enableDarkTheme ? 'dark' : 'light';
  const navColors = clientThemePalette[CLIENT_THEME][colorMode];

  const mergedStaticAndDbConfig = useMemo(() => {
    const temp = mergeNavigationWithDbConfig(navigationConfigData);
    return temp;
  }, [navigationConfigData]);

  const user = useMemo(() => {
    return {
      email,
      customerCode,
      roles: role ? [role] : [],
    };
  }, [email, customerCode, role]);

  const roleVisibleConfig = useMemo(() => {
    if (user?.roles?.length === 0) return [];
    const roleAndAccessFilteredItems = filterVisibleItems(
      mergedStaticAndDbConfig,
      user
    );
    return roleAndAccessFilteredItems;
  }, [user, mergedStaticAndDbConfig]);

  const navMatchers = useMemo(() => {
    return buildNavMatchers(roleVisibleConfig);
  }, [roleVisibleConfig]);

  const inferredModule = useMemo(() => {
    return inferModuleFromNav(location.pathname, navMatchers);
  }, [location.pathname, navMatchers]);

  useEffect(() => {
    if (inferredModule && inferredModule !== selectedModule) {
      dispatch(updateSelectedModuleState(inferredModule));
    }
  }, [dispatch, inferredModule, selectedModule]);

  const visibleSidebarConfig = useMemo(() => {
    const effectiveModule =
      (selectedModule as AppModuleKey | null) ?? inferredModule;
    return filterByModule(roleVisibleConfig, effectiveModule);
  }, [roleVisibleConfig, selectedModule, inferredModule]);

  return (
    <SidebarContainer iscollapsed={iscollapsed} navbarbg={navColors.navbarBg}>
      <div
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'end',
          borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
          borderTop: '1px solid rgba(255, 255, 255, 0.05)',
        }}
        onClick={toggleSidebar}
      >
        <MenuButton navbarhovercolor={navColors.navbarHoverColor}>
          {iscollapsed ? (
            <KeyboardDoubleArrowRight fontSize="small" />
          ) : (
            <KeyboardDoubleArrowLeft fontSize="small" />
          )}
        </MenuButton>
      </div>
      <NavList>
        {visibleSidebarConfig.map((item) => (
          <SidebarItem
            key={item.id}
            item={item}
            iscollapsed={iscollapsed}
            parentId={item.id}
            parentPath={item.path}
            navColors={navColors}
          />
        ))}
      </NavList>
    </SidebarContainer>
  );
});

NewSidebar.displayName = 'NewSidebar';
